import { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Sun, Mountain, Clapperboard, MapPin } from "lucide-react";

const VERTICAL_COLOR = { solar: "#f59e0b", mining: "#0d9488", media: "#7c3aed" };

function pinIcon(vertical) {
  const color = VERTICAL_COLOR[vertical] || "#017471";
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='30' height='42' viewBox='0 0 30 42'><path d='M15 0C6.7 0 0 6.7 0 15c0 10.5 15 27 15 27s15-16.5 15-27C30 6.7 23.3 0 15 0z' fill='${color}'/><circle cx='15' cy='15' r='6' fill='#fff'/></svg>`;
  return L.divIcon({
    className: "oj-map-pin",
    html: svg,
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -38],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 7); return; }
    map.fitBounds(points, { padding: [50, 50] });
  }, [points, map]);
  return null;
}

export default function ProjectsMap({ height = "460px", className = "" }) {
  const { t } = useApp();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const valid = useMemo(() => projects.filter((p) => typeof p.lat === "number" && typeof p.lng === "number"), [projects]);
  const points = useMemo(() => valid.map((p) => [p.lat, p.lng]), [valid]);
  const center = points[0] || [23.2599, 77.4126]; // Bhopal fallback

  const vLabel = (v) => (t.mapP?.verticals?.[v]) || v;

  return (
    <div className={`rounded-2xl overflow-hidden border border-teal-200 shadow-lg ${className}`} data-testid="projects-map">
      <MapContainer center={center} zoom={6} scrollWheelZoom={false} style={{ height, width: "100%" }}>
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {valid.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(p.vertical)}>
            <Popup>
              <div className="min-w-[180px]">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider" style={{ color: VERTICAL_COLOR[p.vertical] || "#017471" }}>
                  {p.vertical === "solar" ? <Sun className="w-3.5 h-3.5" /> : p.vertical === "mining" ? <Mountain className="w-3.5 h-3.5" /> : <Clapperboard className="w-3.5 h-3.5" />}
                  {vLabel(p.vertical)}
                </div>
                <div className="font-extrabold text-[#0B3B39] mt-1 leading-snug">{p.name}</div>
                <div className="flex items-center gap-1 text-xs text-slate-500 mt-1"><MapPin className="w-3 h-3" /> {p.location}</div>
                {p.capacity && <div className="text-xs font-bold text-amber-600 mt-1">{p.capacity}</div>}
                <div className="mt-1">
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${p.status === "operational" ? "bg-teal-50 text-teal-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
